import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useDeviceType } from '../../hooks/useDeviceType';

const IpCard = ({ ipData }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  
  const { isMobile } = useDeviceType();
  
  const cardStyle = {
    background: colors.cardBackground,
    borderRadius: '16px',
    padding: isMobile ? '1.5rem' : '2rem',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)',
    height: 'fit-content'
  };

  const titleStyle = {
    fontSize: isMobile ? '1.2rem' : '1.5rem',
    fontWeight: '600',
    color: colors.text,
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const infoRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    flexDirection: isMobile ? 'column' : 'row',
    padding: isMobile ? '0.6rem 0' : '0.75rem 0',
    borderBottom: `1px solid ${colors.border}`,
    gap: isMobile ? '0.25rem' : '0'
  };

  const labelStyle = {
    fontWeight: '600',
    color: colors.textSecondary,
    minWidth: isMobile ? 'auto' : '120px',
    fontSize: isMobile ? '0.85rem' : '1rem'
  };

  const valueStyle = {
    color: colors.text,
    fontFamily: 'Monaco, Consolas, monospace',
    fontSize: isMobile ? '0.8rem' : '0.9rem',
    wordBreak: 'break-all'
  };

  const highlightStyle = {
    background: colors.primary,
    color: 'white',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: isMobile ? '0.7rem' : '0.8rem',
    fontWeight: '600'
  };

  const getFlag = (country) => {
    const countryFlags = {
      'CN': '🇨🇳', 'US': '🇺🇸', 'JP': '🇯🇵', 'KR': '🇰🇷', 
      'GB': '🇬🇧', 'DE': '🇩🇪', 'FR': '🇫🇷', 'CA': '🇨🇦',
      'AU': '🇦🇺', 'BR': '🇧🇷', 'IN': '🇮🇳', 'RU': '🇷🇺'
    };
    return countryFlags[country] || '🌍';
  };

  if (!ipData) return null;

  return (
    <div style={cardStyle}>
      <h2 style={titleStyle}>
        {t('ipInfo.cardTitle')}
      </h2>
      
      <div>
        <div style={infoRowStyle}>
          <span style={labelStyle}>{t('ipInfo.fields.ip')}</span>
          <span style={valueStyle}>
            <strong>{ipData.ip}</strong>
          </span>
        </div>

        {ipData.hostname && (
          <div style={infoRowStyle}>
            <span style={labelStyle}>{t('ipInfo.fields.hostname')}</span>
            <span style={valueStyle}>{ipData.hostname}</span>
          </div>
        )}

        <div style={infoRowStyle}>
          <span style={labelStyle}>{t('ipInfo.fields.country')}</span>
          <span style={valueStyle}>
            {getFlag(ipData.country)} {ipData.country || 'N/A'}
          </span>
        </div>

        {ipData.region && (
          <div style={infoRowStyle}>
            <span style={labelStyle}>{t('ipInfo.fields.region')}</span>
            <span style={valueStyle}>{ipData.region}</span>
          </div>
        )}

        {ipData.city && (
          <div style={infoRowStyle}>
            <span style={labelStyle}>{t('ipInfo.fields.city')}</span>
            <span style={valueStyle}>{ipData.city}</span>
          </div>
        )}

        {ipData.postal && (
          <div style={infoRowStyle}>
            <span style={labelStyle}>{t('ipInfo.fields.postal')}</span>
            <span style={valueStyle}>{ipData.postal}</span>
          </div>
        )}

        {ipData.loc && (
          <div style={infoRowStyle}>
            <span style={labelStyle}>{t('ipInfo.fields.location')}</span>
            <span style={valueStyle}>
              📍 {ipData.loc}
            </span>
          </div>
        )}

        {ipData.org && (
          <div style={infoRowStyle}>
            <span style={labelStyle}>{t('ipInfo.fields.isp')}</span>
            <span style={valueStyle}>{ipData.org}</span>
          </div>
        )}

        {ipData.timezone && (
          <div style={infoRowStyle}>
            <span style={labelStyle}>{t('ipInfo.fields.timezone')}</span>
            <span style={valueStyle}>
              <span style={highlightStyle}>{ipData.timezone}</span>
            </span>
          </div>
        )}
      </div>

      {ipData.privacy && (
        <div style={{ 
          marginTop: '1rem', 
          padding: isMobile ? '0.8rem' : '1rem', 
          background: colors.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa', 
          borderRadius: '8px' 
        }}>
          <strong style={{ 
            color: colors.textSecondary, 
            fontSize: isMobile ? '0.8rem' : '0.9rem' 
          }}>{t('ipInfo.privacy.title')}</strong>
          <div style={{ 
            fontSize: isMobile ? '0.7rem' : '0.8rem', 
            color: colors.textSecondary, 
            marginTop: '0.5rem',
            lineHeight: '1.4'
          }}>
            {t('ipInfo.privacy.vpn')}: {ipData.privacy.vpn ? t('ipInfo.privacy.yes') : t('ipInfo.privacy.no')} | 
            {t('ipInfo.privacy.proxy')}: {ipData.privacy.proxy ? t('ipInfo.privacy.yes') : t('ipInfo.privacy.no')} | 
            {t('ipInfo.privacy.tor')}: {ipData.privacy.tor ? t('ipInfo.privacy.yes') : t('ipInfo.privacy.no')}
          </div>
        </div>
      )}
    </div>
  );
};

export default IpCard;
